/**
 * Custom React hook for handling profile and location data at the component level.
 */

import { createContext, useContext, useState } from 'react';
import type { Franchise, FranchiseV3 } from '../types/Franchise';
import type { Location } from '../types/Location';
import { type FranchiseUser } from '../types/FranchiseUser';
import { type FranchiseLocation } from '../types/FranchiseLocation';
import { type FranchiseConfiguration } from '../types/FranchiseConfiguration';
import { type GroupResponse } from '@/types/GroupResponse';
import { type UserProfileV3 } from '@/types/UserProfileV3';

// Interface for the data context.
interface DataContext {
  profile: UserProfileV3 | null;
  handleProfileChange: (profile: UserProfileV3, userFranchises: FranchiseV3[]) => void;
  loadingProfile: boolean;
  setLoadingProfile: (loading: boolean) => void;
  userFranchises: FranchiseV3[];
  setUserFranchises: (franchises: FranchiseV3[]) => void;
  franchiseUsers: FranchiseUser[],
  setFranchiseUsers: (franchiseUsers: FranchiseUser[]) => void,
  franchiseLocations: FranchiseLocation[],
  setFranchiseLocations: (franchiseLocations: FranchiseLocation[]) => void,
  franchiseConfiguration: FranchiseConfiguration | null,
  setFranchiseConfiguration: (franchiseConfiguration: FranchiseConfiguration) => void,
  userLocations: Location[];
  setUserLocations: (locations: Location[]) => void;
  selectedFranchise: Franchise | null;
  handleFranchiseChange: (profile: UserProfileV3) => void;
  selectedStackDriveOffFilter: string | null;
  setSelectedStackDriveOffFilter: (filter: string) => void;
  displayedLocations: Location[];
  setDisplayedLocations: (locations: Location[]) => void;
  selectedLocation: Location | null;
  handleSelectedLocation: (location: Location) => void;
  selectedLocations: Location[];
  setSelectedLocations: (locations: Location[]) => void;
  handleSelectedLocationsChange: (id: string) => void;
  selectedAnalysisLocations: Location[];
  setSelectedAnalysisLocations: (locations: Location[]) => void;
  handleSelectedAnalysisLocationsChange: (id: string) => void;
  selectedDates: Date[];
  handleSelectedDates: (dates: Date[]) => void;
  cachedGroups: GroupResponse[];
  setCachedGroups: (groups: GroupResponse[]) => void;
  selectedRegionIds: string[];
  handleSelectedRegionIds: (newRegions: any[], regionSetter: (r: any[]) => void) => void;
  toggled: 'LOBBY' | 'DT';
  handleToggle: (toggled: 'LOBBY' | 'DT') => void;
  handleFilter: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectAll: () => void;
  handleDeselectAll: () => void;
  handleSelectGroups: (group: GroupResponse[], groupLocations: string[], fromPage: string) => void;
  handleSelectAllAnalytics: () => void;
  handleDeselectAllAnalytics: () => void;
  handleSyncAnalytics: () => void;
}

// Dummy context
const dummyContext: DataContext = {
  profile: null,
  handleProfileChange: (profile: UserProfileV3, userFranchises: FranchiseV3[]) => { },
  loadingProfile: true,
  setLoadingProfile: (loading: boolean) => { },
  userFranchises: [],
  setUserFranchises: (franchises: FranchiseV3[]) => { },
  franchiseUsers: [],
  setFranchiseUsers: (franchiseUsers: FranchiseUser[]) => { },
  franchiseLocations: [],
  setFranchiseLocations: (franchiseLocations: FranchiseLocation[]) => { },
  franchiseConfiguration: null,
  setFranchiseConfiguration: (franchiseConfiguration: FranchiseConfiguration) => { },
  userLocations: [],
  setUserLocations: (locations: Location[]) => { },
  selectedFranchise: null,
  handleFranchiseChange: (profile: UserProfileV3) => { },
  selectedStackDriveOffFilter: null,
  setSelectedStackDriveOffFilter: (filter: string) => { },
  displayedLocations: [],
  setDisplayedLocations: (locations: Location[]) => { },
  selectedLocation: null,
  handleSelectedLocation: (location: Location) => { },
  selectedLocations: [],
  handleSelectedLocationsChange: (id: string) => { },
  selectedAnalysisLocations: [],
  setSelectedAnalysisLocations: (locations: Location[]) => { },
  handleSelectedAnalysisLocationsChange: (id: string) => { },
  selectedDates: [new Date(), new Date()],
  handleSelectedDates: (dates: Date[]) => { },
  cachedGroups: [],
  setCachedGroups: (groups: GroupResponse[]) => { },
  selectedRegionIds: [],
  handleSelectedRegionIds: (newRegions: any[], regionSetter: (r: any[]) => void) => { },
  toggled: 'DT',
  handleToggle: (toggled: 'LOBBY' | 'DT') => { },
  handleFilter: (event: React.ChangeEvent<HTMLInputElement>) => { },
  handleSelectAll: () => { },
  handleDeselectAll: () => { },
  handleSelectGroups: (group: GroupResponse[], groupLocations: string[], fromPage: string = '') => { },
  handleSelectAllAnalytics: () => { },
  handleDeselectAllAnalytics: () => { },
  handleSyncAnalytics: () => { },
};

// Create the data context.
const dataContext = createContext(dummyContext);

// Create the data provider.
export const ProvideData: React.FC = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const data = useProvideData();
  return <dataContext.Provider value={data}>{children}</dataContext.Provider>;
};

// Create the useData hook.
export function useData(): DataContext {
  return useContext(dataContext);
}

// Create the useProvideData hook.
const useProvideData = (): DataContext => {
  // const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profile, setProfile] = useState<UserProfileV3 | null>(null);
  const [loadingProfile, setLoadingProfile] = useState<boolean>(true);
  // const [userFranchises, setUserFranchises] = useState<Franchise[]>([]);
  const [userFranchises, setUserFranchises] = useState<FranchiseV3[]>([]);
  const [franchiseUsers, setFranchiseUsers] = useState<FranchiseUser[]>([]);
  const [franchiseLocations, setFranchiseLocations] = useState<
    FranchiseLocation[]
  >([]);
  const [franchiseConfiguration, setFranchiseConfiguration] = useState<
    FranchiseConfiguration | null
  >(null);
  const [userLocations, setUserLocations] = useState<Location[]>([]);
  const [selectedFranchise, setSelectedFranchise] = useState<FranchiseV3 | null>(
    null
  );
  const [displayedLocations, setDisplayedLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null
  );
  const [selectedStackDriveOffFilter, setSelectedStackDriveOffFilter] = useState<string | null>(
    '1h'
  );
  const [selectedLocations, setSelectedLocations] = useState<Location[]>([]);
  const [selectedAnalysisLocations, setSelectedAnalysisLocations] = useState<Location[]>([]);
  const [selectedDates, setSelectedDates] = useState<Date[]>(fetchCachedDates());
  let initToggle = localStorage.getItem('toggled');
  if (initToggle !== 'LOBBY' && initToggle !== 'DT' && initToggle !== "MOBILE") {
    initToggle = 'DT'; // Or migrate old values, e.g., if (initToggle === 'drive-thru') initToggle = 'DT';
  }
  const [toggled, setToggled] = useState<'LOBBY' | 'DT'>(initToggle as 'LOBBY' | 'DT');
  const [cachedGroups, setCachedGroups] = useState<GroupResponse[]>([]);
  const [selectedRegionIds, setSelectedRegionIds] = useState<any[]>([]);

  // Reusable method to get cached dates with TTL
  function fetchCachedDates(ttl: number = 2): Date[] {
    let cachedSelectedDates = localStorage.getItem('selected-dates');
    const cacheTimeStr = localStorage.getItem('selected-dates-cachetime');

    if (cacheTimeStr !== null) {
      const cacheTime = new Date(cacheTimeStr);
      const now = new Date();
      const ttlInMilliseconds = ttl * 60 * 60 * 1000;
      if (now.getTime() - cacheTime.getTime() >= ttlInMilliseconds) {
        // cache time exceeds limit
        cachedSelectedDates = null;
      }
    }

    const yesterday = new Date()
    // yesterday.setDate(new Date().getDate() - 1)

    const returnedDates = cachedSelectedDates !== null
      ? cachedSelectedDates.split(',').map(dateStr => new Date(dateStr))
      : [yesterday, yesterday];

    return returnedDates;
  }



  function handleProfileChange(profile: UserProfileV3, userFranchises: FranchiseV3[]): void {
    setProfile(profile);
    setUserLocations(profile.locations);
    setUserFranchises(userFranchises.sort((a, b) => a.displayName.localeCompare(b.displayName)));
    if (userFranchises.length === 0) {
      return;
    }

    // Set the selected regions from cache
    const cachedSelectedRegions = localStorage.getItem('selected-region-ids');
    setSelectedRegionIds(
      cachedSelectedRegions ? cachedSelectedRegions.split(',') : []
    );
    // Set the selected dates from cache
    setSelectedDates(fetchCachedDates());

    setSelectedFranchise(profile.franchise);
    setDisplayedLocations(profile.locations);
    setLoadingProfile(false);

    // set selected location
    const cachedSingleLocation = localStorage.getItem('selected-single-location');
    let foundLocation;

    if (typeof cachedSingleLocation === 'string') {
      foundLocation = profile.locations.find(
        (location) => (location.id === cachedSingleLocation && location.franchiseId === profile.franchise.id)
      );
    }

    if (foundLocation === undefined || foundLocation === null) {
      foundLocation = profile.locations[0];
    }

    setSelectedLocation(foundLocation ?? null);
    setSelectedStackDriveOffFilter('1h');
    // Set the selected locations to the franchise's locations if they're cached, else all of them.
    const cachedLocationIds = localStorage
      .getItem(`franchise-${profile.franchise.id}-selected-location-ids`)
      ?.split(',');
    if (cachedLocationIds !== undefined) {
      setSelectedLocations(
        profile.locations.filter((location) =>
          cachedLocationIds.includes(location.id)
        )
      );
      return;
    }
    setSelectedLocations(profile.locations);
  }

  // Handle selected regions change
  function handleSelectedRegionIds(newRegions: any[], regionSetter: (r: any[]) => void): void {
    var regionsToSet = newRegions
    if (newRegions.length !== 0) {
      if (newRegions.length > 1 && newRegions.filter(reg => reg.id == "ALL").length != 0) {
        regionsToSet = newRegions.filter(reg => reg.id == "ALL")
      }
      localStorage.setItem('selected-region-ids', regionsToSet.map(reg => reg.id).join(','));

    }

    regionSetter(regionsToSet);
    const cachedSelectedRegions = localStorage.getItem('selected-region-ids');
    setSelectedRegionIds(
      cachedSelectedRegions ? cachedSelectedRegions.split(',') : []
    );
  }

  // Handle single location change
  function handleSelectedLocation(location: Location): void {
    setSelectedLocation(location);
    localStorage.setItem('selected-single-location', location.id);
  }

  // Handle selected date change
  function handleSelectedDates(dates: Date[]): void {
    setSelectedDates(dates);
    localStorage.setItem('selected-dates', dates.map(date => date.toISOString()).join(','));
    localStorage.setItem('selected-dates-cachetime', new Date().toISOString());
  }

  // Handle toggle change.
  function handleToggle(toggled: 'LOBBY' | 'DT'): void {
    setToggled(toggled);
    // Cache the toggled state.
    localStorage.setItem('toggled', toggled);
  }

  function handleFranchiseChange(profile: UserProfileV3): void {
    localStorage.setItem('selected-franchise-id', profile.franchise.id);
    setProfile(profile);
    setSelectedFranchise(profile.franchise);
    setUserLocations(profile.locations);
    setDisplayedLocations(profile.locations);
    const foundLocation = profile.locations.find(
      (location) => location.franchiseId === profile.franchise.id
    );
    setSelectedLocation(foundLocation ?? null);
    setSelectedLocations(profile.locations);
    // Remove region cache on franchise change
    localStorage.removeItem('selected-region-ids');
  }

  // Handle filtering.
  function handleFilter(event: React.ChangeEvent<HTMLInputElement>): void {
    event.preventDefault();
    const val = event.target.value.toLowerCase();

    const filteredLocations = userLocations.filter((location) => {
      if (location.franchiseId !== selectedFranchise?.id) {
        return false;
      }
      if (location.displayName.toLowerCase().includes(val)) {
        return true;
      }

      if (location.franchiseName.toLowerCase().includes(val)) {
        return true;
      }

      return false;
    });

    setDisplayedLocations(filteredLocations);
  }

  // Handle select all.
  function handleSelectAll(): void {
    const filteredLocations = userLocations.filter((location) => (
      location.franchiseId === selectedFranchise?.id
    ));
    setSelectedLocations(filteredLocations);

    // Cache the selected location ids.
    const cachedLocationIds = filteredLocations.map(
      (location) => location.id
    );
    localStorage.setItem(
      `franchise-${selectedFranchise?.id}-selected-location-ids`,
      cachedLocationIds.join(',')
    );
  }

  // Handle select all.
  function handleDeselectAll(): void {
    setSelectedLocations([] as Location[]);
    localStorage.setItem(`franchise-${selectedFranchise?.id}-selected-location-ids`, '');
  }

  // Handle select group
  function handleSelectGroups(groups: GroupResponse[], groupLocations: string[], fromPage: string = ''): void {
    const filteredLocations = userLocations.filter((location) =>
      groupLocations.includes(location.id)
    );
    handleDeselectAll();
    setSelectedLocations(filteredLocations);
    // Cache the selected location ids.
    const cachedLocationIds = filteredLocations.map(
      (location) => location.id
    );
    localStorage.setItem(
      `franchise-${selectedFranchise?.id}-selected-location-ids`,
      cachedLocationIds.join(',')
    );
    if (fromPage === 'analytics') {
      handleSelectAllAnalytics();
      setSelectedAnalysisLocations(filteredLocations);
      localStorage.setItem(
        `franchise-${selectedFranchise?.id}-selected-analysis-location-ids`,
        filteredLocations.map((location) => location.id).join(',')
      );
    }
    // cache selected group objects (array)
    setCachedGroups(groups); // or adjust as needed for multiple groups
  }

  // Handle select all for analytics.
  function handleSelectAllAnalytics(): void {
    const filteredLocations = userLocations.filter((location) => (
      selectedLocations.includes(location) // only within the selected locations
    ));
    setSelectedAnalysisLocations(filteredLocations);
  }

  // Handle select all for analytics.
  function handleDeselectAllAnalytics(): void {
    setSelectedAnalysisLocations([] as Location[]);
  }

  // Handle syncing detailed and main location selector
  function handleSyncAnalytics(): void {
    setSelectedAnalysisLocations(selectedLocations);
  }

  // Handle selected location change.
  function handleSelectedLocationsChange(id: string): void {
    const locationInSelected = selectedLocations.find(
      (location) => location.id === id
    );
    if (locationInSelected !== undefined) {
      const newSelectedLocations = selectedLocations.filter(
        (location) => location.id !== id
      );
      console.log(newSelectedLocations);
      setSelectedLocations(newSelectedLocations);
      // Cache the selected location ids.
      const cachedLocationIds = newSelectedLocations.map(
        (location) => location.id
      );
      localStorage.setItem(
        `franchise-${selectedFranchise?.id}-selected-location-ids`,
        cachedLocationIds.join(',')
      );
      if (cachedGroups.length !== 0) {
        // manual changes to locations nullifies group selection
        setCachedGroups([]);
      }
      return;
    }
    const location = profile?.locations.find(
      (location: Location) => location.id === id
    );
    if (location === undefined) {
      return;
    }
    setSelectedLocations([...selectedLocations, location]);
    // Cache the selected location ids.
    const cachedLocationIds = [...selectedLocations, location].map(
      (location) => location.id
    );
    localStorage.setItem(
      `franchise-${selectedFranchise?.id}-selected-location-ids`,
      cachedLocationIds.join(',')
    );
    if (cachedGroups.length !== 0) {
      // manual changes to locations nullifies group selection
      setCachedGroups([]);
    }
  }

  // Handle selected analysis location change.
  function handleSelectedAnalysisLocationsChange(id: string): void {
    const locationInSelected = selectedAnalysisLocations.find(
      (location) => location.id === id
    );
    if (locationInSelected !== undefined) {
      const newSelectedLocations = selectedAnalysisLocations.filter(
        (location) => location.id !== id
      );
      setSelectedAnalysisLocations(newSelectedLocations);
      // Cache the selected location ids.
      const cachedLocationIds = newSelectedLocations.map(
        (location) => location.id
      );
      localStorage.setItem(
        `franchise-${selectedFranchise?.id}-selected-analysis-location-ids`,
        cachedLocationIds.join(',')
      );
      return;
    }
    const location = profile?.locations.find(
      (location: Location) => location.id === id
    );
    if (location === undefined) {
      return;
    }
    setSelectedAnalysisLocations([...selectedAnalysisLocations, location]);
    // Cache the selected analysis location ids.
    const cachedLocationIds = [...selectedAnalysisLocations, location].map(
      (location) => location.id
    );
    localStorage.setItem(
      `franchise-${selectedFranchise?.id}-selected-analysis-location-ids`,
      cachedLocationIds.join(',')
    );
  }

  return {
    profile,
    handleProfileChange,
    loadingProfile,
    setLoadingProfile,
    userFranchises,
    setUserFranchises,
    franchiseUsers,
    setFranchiseUsers,
    franchiseLocations,
    setFranchiseLocations,
    franchiseConfiguration,
    setFranchiseConfiguration,
    userLocations,
    setUserLocations,
    selectedFranchise,
    handleFranchiseChange,
    displayedLocations,
    setDisplayedLocations,
    selectedStackDriveOffFilter,
    setSelectedStackDriveOffFilter,
    selectedLocation,
    handleSelectedLocation,
    selectedLocations,
    setSelectedLocations,
    handleSelectedLocationsChange,
    selectedAnalysisLocations,
    setSelectedAnalysisLocations,
    handleSelectedAnalysisLocationsChange,
    selectedDates,
    handleSelectedDates,
    cachedGroups,
    setCachedGroups,
    selectedRegionIds,
    handleSelectedRegionIds,
    toggled,
    handleToggle,
    handleFilter,
    handleSelectAll,
    handleDeselectAll,
    handleSelectGroups,
    handleSelectAllAnalytics,
    handleDeselectAllAnalytics,
    handleSyncAnalytics,
  };
};
